import React from 'react';
import { useNavigate } from 'react-router';
import { LogOut, User, UserCircle, Sun, Moon, Monitor, Truck, PanelLeft } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useTheme, type ThemeMode } from '@/hooks/use-theme';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/features/notifications/notification-bell';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

function ThemeToggle() {
  const { mode, resolved, setMode } = useTheme();
  // Show the icon for the *resolved* theme (sun when light is showing, moon
  // when dark) so the button is a visual mirror of current state. Click opens
  // a dropdown to pick Light / Dark / System.
  const ResolvedIcon = resolved === 'dark' ? Moon : Sun;
  const Item = ({ value, label, Icon }: { value: ThemeMode; label: string; Icon: React.ComponentType<{ className?: string }> }) => (
    <DropdownMenuItem onClick={() => setMode(value)} className={mode === value ? 'bg-accent' : ''}>
      <Icon className="h-4 w-4" /> {label}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Toggle theme">
          <ResolvedIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        <Item value="light"  label="Light"  Icon={Sun} />
        <Item value="dark"   label="Dark"   Icon={Moon} />
        <Item value="system" label="System" Icon={Monitor} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header({ onToggleSidebar }: { onToggleSidebar?: () => void }) {
  const { employee, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/auth/login');
  }

  // min-h-14 (not fixed h-14) + top safe-area padding: on iOS the header
  // background fills the notch/status-bar area while its content stays a 56px
  // row below the inset. env() is 0 on web/Android → unchanged there.
  return (
    <header className="min-h-14 pt-[env(safe-area-inset-top)] border-b bg-background flex items-center justify-between px-4 md:px-6 sticky top-0 z-10">
      <div className="flex items-center gap-2 min-w-0">
        {/* Collapse/expand the static sidebar — only relevant on lg+, where
            the sidebar is shown. Below lg the bottom-nav handles navigation. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Toggle sidebar"
          onClick={onToggleSidebar}
          className="hidden lg:inline-flex shrink-0"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
        {/* Logo + brand — shown below lg (phones + tablets); on lg+ the
            sidebar displays the brand instead. */}
        <div className="flex items-center gap-2 lg:hidden min-w-0">
          <Truck className="h-5 w-5 text-primary shrink-0" />
          <div className="font-semibold text-base truncate">Atlas Fleet</div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {/* Desktop only — on phones/tablets the bottom-nav "Notifications" tab
            opens the full /notifications page instead of this popup. */}
        <span className="hidden lg:inline-flex">
          <NotificationBell />
        </span>
        <ThemeToggle />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2">
            <User className="h-4 w-4" />
            <span className="max-w-[200px] truncate">{employee?.fullName || employee?.email}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span>{employee?.fullName}</span>
            <span className="text-xs font-normal text-muted-foreground">{employee?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <UserCircle className="h-4 w-4" /> Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive">
            <LogOut className="h-4 w-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );
}
